import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Bot,
  Clock,
  Database,
  LayoutDashboard,
  MessageSquare,
  MoreVertical,
  PanelsTopLeft,
  Play,
  Settings,
  Trash2,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { BrandCard, TINTS } from "../integrations/brand";
import { GRIGIO, SCURO } from "../integrations/casella";
import { useTranslation } from "../../hooks/useTranslation";

/**
 * La scheda di un assistente, uguale nella Dashboard e nella pagina
 * Assistenti: nome e stato, descrizione, conversazioni e ultimo
 * aggiornamento, poi «Apri» e «Prova». Il menu porta alle sue pagine.
 */

const STATI = {
  active: { chiave: "botsList.statusActive", dot: "bg-[#19D163]", cls: "bg-[#19D163]/10 text-[#0F7A3D]" },
  inactive: { chiave: "botsList.statusInactive", dot: "bg-[#94A3B8]", cls: "bg-[#F1F5F9] text-[#475569]" },
  processing: { chiave: "botsList.statusProcessing", dot: "bg-blue-500 animate-pulse", cls: "bg-blue-50 text-blue-700" },
  error: { chiave: "botsList.statusError", dot: "bg-red-500", cls: "bg-red-50 text-red-700" },
};

/** Lo stato dell'assistente in una pillola: pallino colorato e parola. */
export function StatoAssistente({ stato }) {
  const { t } = useTranslation();
  const s = STATI[stato] || STATI.inactive;
  return (
    <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ${s.cls}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} aria-hidden="true" />
      {STATI[stato] ? t(s.chiave) : stato || t(s.chiave)}
    </span>
  );
}

/** Quando, a parole: oggi, ieri, 3 giorni fa, poi la data. */
export function quando(data, t, lingua) {
  if (!data) return null;
  const giorno = new Date(data);
  if (Number.isNaN(giorno.getTime())) return null;
  const inizio = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const giorni = Math.round((inizio(new Date()) - inizio(giorno)) / 86400000);
  if (giorni <= 0) return t("botsList.whenToday");
  if (giorni === 1) return t("botsList.whenYesterday");
  if (giorni < 7) return t("botsList.whenDaysAgo", { count: giorni });
  return giorno.toLocaleDateString(lingua, { day: "numeric", month: "short", year: "numeric" });
}

/** L'icona dell'assistente: il robot sul gradiente dell'onboarding. */
export function IconaAssistente() {
  return (
    <span
      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-white shadow-[0_4px_10px_rgba(15,23,42,0.12)]"
      style={{ backgroundImage: TINTS.violet }}
      aria-hidden="true"
    >
      <Bot className="h-5 w-5" strokeWidth={1.75} />
    </span>
  );
}

export function SchedaAssistente({ bot, onElimina, indice = 0 }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const pagina = `/bots/${bot._id}`;
  const conversazioni = bot.stats?.total_conversations || 0;
  const aggiornato = quando(bot.last_ingest_at || bot.updatedAt, t, i18n.language);

  const voci = [
    { icona: LayoutDashboard, testo: t("nav.overview"), vai: pagina },
    { icona: Database, testo: t("nav.sources"), vai: `${pagina}/sources` },
    { icona: PanelsTopLeft, testo: t("nav.widget"), vai: `${pagina}/widget` },
    { icona: MessageSquare, testo: t("nav.conversations"), vai: `${pagina}/conversations` },
    { icona: Settings, testo: t("common.settings"), vai: `${pagina}/settings` },
  ];

  return (
    <BrandCard
      className="pu-carta-in flex min-w-0 flex-col p-5 transition-shadow hover:shadow-[0_10px_30px_rgba(15,23,42,0.08)]"
      style={{ "--ritardo": `${Math.min(indice, 8) * 40}ms` }}
    >
      <div className="flex items-start gap-3">
        <IconaAssistente />
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-base font-semibold text-[#020617]">
            <Link to={pagina} className="rounded-sm hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
              {bot.name}
            </Link>
          </h3>
          <div className="mt-1">
            <StatoAssistente stato={bot.status} />
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={t("botsList.actionsFor", { name: bot.name })}
              className="-mr-1.5 -mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] text-[#94A3B8] transition-colors hover:bg-[#F1F5F9] hover:text-[#334155] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <MoreVertical className="h-4 w-4" aria-hidden="true" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48 rounded-xl">
            {voci.map(({ icona: Icona, testo, vai }) => (
              <DropdownMenuItem key={vai} onClick={() => navigate(vai)}>
                <Icona className="mr-2 h-4 w-4" aria-hidden="true" />
                {testo}
              </DropdownMenuItem>
            ))}
            {onElimina ? (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="text-red-600 focus:text-red-600" onClick={() => onElimina(bot)}>
                  <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" />
                  {t("common.delete")}
                </DropdownMenuItem>
              </>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <p className={`mt-4 line-clamp-2 min-h-[40px] text-sm leading-5 ${bot.description ? "text-[#475569]" : "text-[#94A3B8]"}`}>
        {bot.description || t("botsList.noDescription")}
      </p>

      <p className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[#64748B]">
        <span className="inline-flex items-center gap-1.5 tabular-nums">
          <MessageSquare className="h-3.5 w-3.5" aria-hidden="true" />
          {t("botsList.conversationsCount", { count: conversazioni })}
        </span>
        {aggiornato ? (
          <span className="inline-flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5" aria-hidden="true" />
            {t("botsList.updatedWhen", { when: aggiornato })}
          </span>
        ) : null}
      </p>

      <div className="mt-5 flex gap-2 border-t border-[#F1F5F9] pt-4">
        <Link to={pagina} className={`${GRIGIO} flex-1`}>
          {t("botsList.open")}
        </Link>
        <Link to={`${pagina}/playground`} className={`${SCURO} flex-1`}>
          <Play className="h-3.5 w-3.5" aria-hidden="true" />
          {t("botsList.try")}
        </Link>
      </div>
    </BrandCard>
  );
}
